import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  useNotify,
  useRefresh,
  useRecordContext,
  useDataProvider,
} from "react-admin";
import { RISK_LIKELIHOOD_CHOICES } from "../consts";

interface AddMitigationModalProps {
  open: boolean;
  onClose: () => void;
}

const AddMitigationModal: React.FC<AddMitigationModalProps> = ({
  open,
  onClose,
}) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [description, setDescription] = useState("");
  const [completed, setCompleted] = useState(true);
  const [mitigatedLikelihood, setMitigatedLikelihood] = useState(
    record?.mitigatedLikelihood || ""
  );
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      const totalFiles = files.length + newFiles.length;

      if (totalFiles > 10) {
        notify("Maximum 10 files allowed", { type: "warning" });
        return;
      }

      setFiles([...files, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      notify("Description is required", { type: "warning" });
      return;
    }

    if (!record?.id) {
      notify("Risk ID not found", { type: "error" });
      return;
    }

    if (completed && !mitigatedLikelihood) {
      notify("Mitigated Likelihood is required to complete mitigation", {
        type: "warning",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("description", description);
      formData.append("completed", completed.toString());
      formData.append("mitigatedLikelihood", mitigatedLikelihood);

      // Add all files to the form data
      files.forEach((file) => {
        formData.append("artifact", file);
      });

      await (dataProvider as any).addMitigation(record.id, formData);

      notify("Mitigation added successfully", { type: "success" });
      setDescription("");
      setCompleted(true);
      setFiles([]);
      refresh();
      onClose();
    } catch (error: any) {
      notify(error.message || "Failed to add mitigation", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDescription("");
      setFiles([]);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Mitigate
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "left",
            gap: 2,
            height: "3em",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={completed}
                onChange={(e) => setCompleted(e.target.checked)}
                disabled={loading}
              />
            }
            label="Mitigation Complete"
            sx={{ width: "300px" }}
          />
          {/* If completed is true, show mitigationLiklihood dropdown */}
          {completed && (
            <FormControl
              fullWidth
              disabled={loading}
              sx={{ margin: 0, paddingTop: "0.2em" }}
            >
              <InputLabel
                id="mitigation-likelihood-label"
                sx={{ paddingTop: "0.2em" }}
              >
                Mitigation Likelihood
              </InputLabel>
              <Select
                labelId="mitigation-likelihood-label"
                value={mitigatedLikelihood}
                label="Mitigation Likelihood"
                onChange={(e) => setMitigatedLikelihood(e.target.value)}
                sx={{
                  maxWidth: "250px",
                }}
              >
                {RISK_LIKELIHOOD_CHOICES.map((choice) => (
                  <MenuItem key={choice.id} value={choice.id}>
                    {choice.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Description"
            multiline
            rows={4}
            fullWidth
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            helperText="Describe the mitigation action taken"
          />
          <Box>
            <Button
              variant="outlined"
              component="label"
              disabled={loading || files.length >= 10}
            >
              Upload Artifact Files (Optional)
              <input
                type="file"
                hidden
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx,.xls,.doc,.docx,.txt"
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Maximum 10 files. Supported formats: PDF, images, CSV, Excel,
              Word, text files
            </Typography>
          </Box>

          {files.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({files.length}/10):
              </Typography>
              <List dense>
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveFile(index)}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(2)} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !description.trim()}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMitigationModal;
